import { ProjectPositionNumber } from '@schuettflix/interfaces';
import { usePositionContext } from '@/shared/context/PositionContext.tsx';
import { EditPositionSummary } from '@/modules/position/components/PositionSummary/components/EditPositionSummary.tsx';
import { CreatePositionSummary } from '@/modules/position/components/PositionSummary/components/CreatePositionSummary.tsx';
import { usePlatformOrganizationId } from '@/clients/platformInfo/usePlatformInfo.ts';
import { FinanceSettingsForm } from '@/shared/forms/FinanceSettingsForm/FinanceSettingsForm.tsx';
import { FinanceSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import { constructPositionConfiguration } from '@/modules/position/components/PositionSummary/utils/constructPositionConfiguration.ts';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { useToasterContext } from '@/shared/context/ToasterContext.tsx';
import { useTranslation } from 'react-i18next';
import { useMonolithFrontendUrl } from '@/shared/hooks/useMonolithFrontendUrl.ts';
import { getPositionOverviewMonolithRoute } from '@/shared/utils/positionRoutes.ts';
import { encodeBase64Json } from '@/shared/utils/base64Json.ts';
import { useContext } from 'react';
import { BeforeUnloadContext } from '@/shared/context/BeforeUnloadContext.tsx';
import { useNavigate } from 'react-router';

export type FinanceSettingsFormRendererProps = {
    isPending: boolean;
    isSuccess: boolean;
    onSubmit: (
        positionConfiguration: ReturnType<typeof constructPositionConfiguration>
    ) => Promise<{ id: string; positionNumber: ProjectPositionNumber }>;
};

export function PositionSummary() {
    const { t } = useTranslation();
    const { marketCode } = useMarketContext();
    const { isEditing, positionId, contextData } = usePositionContext();
    const { addToast } = useToasterContext();
    const navigate = useNavigate();

    const platformOrganizationId = usePlatformOrganizationId();
    const beforeUnloadContext = useContext(BeforeUnloadContext);

    const formData = contextData.getFormData();
    const customerInfo = contextData.getCustomerInformation();
    const businessCase = contextData.getBusinessCase();
    const productConfigurations = contextData.getProductConfigurations();
    const financeSettings = contextData.getFinanceSettings();

    const { openUrl } = useMonolithFrontendUrl({
        target: 'currentTab',
        historyMethod: 'push',
    });

    if (!customerInfo || !productConfigurations || !businessCase) {
        throw new Error(
            'customerInfo, productConfigurations and businessCase are required to render NewManualFulfillmentPositionSummary'
        );
    }

    const FinanceSettingsFormRenderer = ({ isSuccess, onSubmit, isPending }: FinanceSettingsFormRendererProps) => {
        const handleSubmit = async (financeSettings: FinanceSettings[]) => {
            beforeUnloadContext.setEnabled(false);

            try {
                contextData.setFinanceSettings(financeSettings);

                const positionConfiguration = constructPositionConfiguration({
                    formData,
                    customerInfo,
                    businessCase,
                    financeSettings,
                    productConfigurations,
                    marketCode,
                    platformOrganizationId,
                });

                const salesforceOpportunityId = formData.salesforceOpportunityId;
                const position = await onSubmit(positionConfiguration);

                if (!isEditing && salesforceOpportunityId) {
                    addToast({
                        title: t('positions.successfully-created', {
                            positionNumber: position.positionNumber,
                        }),
                        type: 'success',
                    });
                    window.history.replaceState({}, '');
                    void navigate(
                        `/import/deal/${salesforceOpportunityId}/?projectNumber=${customerInfo.constructionProjectId}`
                    );
                    return;
                }

                openUrl({
                    path: getPositionOverviewMonolithRoute(customerInfo.constructionProjectId, position.id),
                    query: {
                        projectPositionForced: encodeBase64Json({ status: 'open' }),
                    },
                    toast: {
                        type: 'success',
                        message: t(isEditing ? 'positions.successfully-updated' : 'positions.successfully-created', {
                            positionNumber: position.positionNumber,
                        }),
                    },
                });
            } catch (error) {
                console.error(error);

                addToast({
                    type: 'error',
                    title: t('positions.created.error'),
                });
            }
        };

        return (
            <FinanceSettingsForm
                defaultValues={financeSettings}
                customerOrganizationId={customerInfo.organizationId}
                productConfigurations={productConfigurations}
                isSubmissionPending={isPending}
                isSubmissionSuccessful={isSuccess}
                onChange={financeSetting => {
                    contextData.upsertFinanceSetting(financeSetting);
                }}
                onSubmit={handleSubmit}
                mode={isEditing ? 'edit' : 'create'}
                submitButtonLabel={
                    isEditing ? t('position.summary.savePosition') : t('position.summary.createNewPosition')
                }
            />
        );
    };

    if (isEditing) {
        return (
            <EditPositionSummary positionId={positionId} FinanceSettingsFormRenderer={FinanceSettingsFormRenderer} />
        );
    } else {
        return <CreatePositionSummary FinanceSettingsFormRenderer={FinanceSettingsFormRenderer} />;
    }
}
